
import { useState, useEffect, useRef, useContext, createContext, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";


import { useUser } from "./UserContext";
import { useSubjectsMetadata } from "./SubjectsMetadataContext";

import { fromKebabCaseToCamelCase, toCamelCase, toKebabCase } from "../utilities/helpers";
import { fetchProgramFeatureData } from "../utilities/server-fetch";

import { EngagementTypeSelector, FeatureTypeSelector, ProgramSelector, SubjectSelector } from "@Beasted1010/pivotal-wisdom";


export {
  CoachingContextProvider,
  useCoaching, useCoachingUpdate
};


const CoachingContext = createContext<any>( null ); // TODO: Properly fill out
const CoachingUpdateContext = createContext<any>( null );


function useCoaching() {
  return useContext( CoachingContext );
}

function useCoachingUpdate() {
  return useContext( CoachingUpdateContext );
}






type CoachingContextProps = {
  children?: JSX.Element | JSX.Element[];

  engagementSelector: EngagementTypeSelector;
  optionSelector: string;

  defaults?: any; // TODO: TYPE ???
}


// TODO: Rename to SubjectContext ??? - But it does only have subjectData out of necessity to get programData
function CoachingContextProvider( {engagementSelector, optionSelector, defaults, children}: CoachingContextProps ) {
  // Provider handles...

  // ... Creating State
  const navigate = useNavigate();

  /*
  const [ searchParams, setSearchParams ] = useSearchParams();
  const subject = fromKebabCaseToCamelCase(searchParams.get("subject") as string) as SubjectSelector;
  const program = fromKebabCaseToCamelCase(searchParams.get("program") as string) as ProgramSelector;
  const service = fromKebabCaseToCamelCase(searchParams.get("service") as string);
  console.log("... SEARCH PARAMS", subject, program, service);
  */


  const [ selectedEngagement, setSelectedEngagement ] = useState<EngagementTypeSelector>( engagementSelector );
  const [ selectedOption, setSelectedOption ] = useState<string>( optionSelector );

  const [ coachingData, setCoachingData ] = useState<ICoaching | undefined>( undefined );
  const [ engagementsData, setEngagementsData ] = useState<IEngagement[] | undefined>( undefined );
  const [ engagementOptions, setEngagementOptions ] = useState<IEngagementOption[] | undefined>( undefined );

  const [ optionContent, setOptionContent ] = useState<IEngagementOption | undefined>( undefined );

  const [ programCoachingData, setProgramCoachingData ] = useState<any>(); // TODO: TYPE
  const [ selectedOffer, setSelectedOffer ] = useState<any>(); // TODO: TYPE


  const [ subjects, setSubjects ] = useState<string[]>();
  const [ topics, setTopics ] = useState<any>(); // TODO: TYPE
  const [ selectedSubject, setSelectedSubject ] = useState<SubjectSelector | undefined>( defaults?.selectedSubject );
  const [ selectedTopic, setSelectedTopic ] = useState<ProgramSelector | undefined>( defaults?.selectedTopic ); // TODO: Decide if want all Programs or a separate "Topic Selector" for more general functionality (?) -- and more granular... Should this data be what's listed in the JSON for Coaching > Schedule ? Or perhaps at Top-Level of Coaching for more broad use?
  const [ selectedService, setSelectedService ] = useState<string | undefined>( defaults?.selectedService );

  const { specialtySubjectsMetadata, foundationSubjectsMetadata } = useSubjectsMetadata();


  const [loading, setLoading] = useState( true );

  const { user } = useUser();


  // ... Updating State
  /*
  useEffect( () => {
    setSearchParams({
      subject: toKebabCase(selectedSubject as string),
      program: toKebabCase(selectedTopic as string),
      service: toKebabCase(selectedService as string)
    });
    console.log("UPDATED SEARCH PARAMS...", selectedSubject, selectedTopic, selectedService);
  }, [selectedSubject, selectedTopic, selectedService] );
  */

  useEffect( () => {
    const abortController = new AbortController();

    const updateData = async () => {
      const newCoachingData: ICoaching = require(`../data/mock-coaching.json`);

      if( !newCoachingData ) {
        console.error( "ERROR: No Coaching Data found" ); // TODO: ERROR HANDLE
        // ... ?
      }

      setCoachingData( newCoachingData );

      const engagements = newCoachingData.engagements;

      setEngagementsData( engagements );

      // TODO: Will "Loading" as part of Dependency Array cause repeated renders?
      setLoading( false );
    }

    updateData();

    return () => {
      abortController.abort();
    }
  }, [] );

  useEffect( () => {
    setSelectedEngagement( engagementSelector );
  }, [engagementSelector] );

  useEffect( () => {
    setSelectedOption( optionSelector );
  }, [optionSelector] );

  useEffect( () => {
    if( !engagementsData ) return;

    const currentEngagement = engagementsData.find( (engagement: IEngagement) => {
      return engagement.selector === selectedEngagement;
    });

    if( currentEngagement == null ) {
      return; // TODO: ERROR HANDLE
    }

    const engagementOptions = currentEngagement.options;
    setEngagementOptions( engagementOptions );
  }, [engagementsData, selectedEngagement]);
    

  useEffect( () => {
    if( !engagementOptions ) return;

    selectOption( selectedOption, engagementOptions );
  }, [engagementOptions, selectedOption]);

  useEffect( () => {
    if( subjects && subjects.length > 0 ) selectSubject( defaults?.selectedSubject ?? subjects?.[0] );
  }, [subjects, topics]);

  useEffect( () => {
    const subjectsMetadata = [ ...specialtySubjectsMetadata, ...foundationSubjectsMetadata ];

    const { subjectSelectors, topicSelectors } = subjectsMetadata.reduce( (accumulator, subjectMetadata: ISubjectMetadata) => {
      const subjectSelector = subjectMetadata.selector;
      accumulator.subjectSelectors.push( subjectSelector );

      if( !accumulator.topicSelectors[subjectSelector] ) {
        accumulator.topicSelectors[subjectSelector] = [];
      }

      const currentTopicSelectors = subjectMetadata.programs.map( program => program.selector );
      accumulator.topicSelectors[subjectSelector].push( ...currentTopicSelectors );

      return accumulator;
    }, { subjectSelectors: [], topicSelectors: {} });

    setSubjects( subjectSelectors );
    setTopics( topicSelectors );
  }, [specialtySubjectsMetadata, foundationSubjectsMetadata] );




  const selectEngagement = useCallback( (engagementToSelect: EngagementTypeSelector) => {

    const selector = toCamelCase( engagementToSelect ) as EngagementTypeSelector;
    setSelectedEngagement( selector );

    const engagement: IEngagement | undefined = engagementsData?.find( (engagement: IEngagement) => {
      return engagement.selector == engagementToSelect;
    });

    if( engagement == null ) {
      return; // TODO: ERROR HANDLE
    }

    const engagementOptions = engagement.options;
    const engagementOption = engagementOptions?.length > 0 ? engagementOptions[0] : undefined;
    const engagementOptionSelector = engagementOption?.selector;

    setEngagementOptions( engagementOptions );
    setOptionContent( engagementOption );

    selectOption( engagementOptionSelector, engagementOptions, engagement.selector );
  }, [engagementsData]);

  const selectOption = useCallback( (optionToSelect: string, existingEngagementOptions?: IEngagementOption[], newEngagementSelected?: EngagementTypeSelector) => { //IEngagementOption ) => { // TODO: Fix inconsistency of naming (Option vs Item...)

    const options = existingEngagementOptions ?? engagementOptions;
    const engagementSelector: EngagementTypeSelector = newEngagementSelected ?? selectedEngagement;

    if( !options ) return; // TODO: ERROR HANDLE

    const newOption: IEngagementOption | undefined = options.find( (option: IEngagementOption) => {
      return option.selector == optionToSelect; // TODO: Make this ACTUAL SELECTOR (current "string" Type)
    });

    setSelectedOption( optionToSelect );
    setOptionContent( newOption );


    /*
    const subject = fromKebabCaseToCamelCase(searchParams.get("subject") as string) as SubjectSelector;
    const program = fromKebabCaseToCamelCase(searchParams.get("program") as string) as ProgramSelector;
    const service = fromKebabCaseToCamelCase(searchParams.get("service") as string);
    const searchQuery = `?subject=${toKebabCase(subject as string)}&program=${toKebabCase(program as string)}&service=${toKebabCase(service as string)}`;
    navigate( `/content/coaching/${toKebabCase(engagementSelector)}/${toKebabCase(optionToSelect)}${searchQuery}` );
    */

    if( !defaults?.useAsStandaloneComponent ) {
      navigate( `/content/coaching/${toKebabCase(engagementSelector)}/${toKebabCase(optionToSelect)}` );
    }
  }, [engagementOptions, selectedEngagement, defaults]);




  const updateProgramCoachingData = async ( topicSelector: ProgramSelector, subjectSelector: SubjectSelector ) => { // TODO: MOCKING FOR THINKING
    if( !subjectSelector || !topicSelector ) return; // TODO: Name "selectedProgram" instead?

    const abortController: AbortController = new AbortController();

    try {
      // TODO: ... PERFORMANCE CONCERN (fetching with each selection of "Topic" in DropDown)
      const coachingFeature: IFeature = await fetchProgramFeatureData( {subjectSelector: subjectSelector, programSelector: topicSelector, featureSelector: FeatureTypeSelector.COACHING, signal: abortController.signal} );

      setProgramCoachingData( coachingFeature.content );
      setSelectedOffer( coachingFeature.content?.offers[0] );

      selectService( coachingFeature.content?.offers[0]?.serviceSelector );
    } catch(error) {
      abortController.abort();
      console.error(`ERROR fetching Feature Data for Coaching Feature, given Subject=${selectedSubject} and Program=${topicSelector}`, error);
    }
  }


  const selectSubject = useCallback( (subjectSelector: string | SubjectSelector) => {
    if( !subjectSelector ) return;

    const selector = toCamelCase(subjectSelector) as SubjectSelector;
    setSelectedSubject( selector );

    if( topics ) {
      const subjectTopics = topics[selector] || []

      /*
      const subjectTopic = subjectTopics.find( (topic: string) => {
        return topic === program;
      }) ?? subjectTopics[0];
      */
      const subjectTopic = defaults?.selectedTopic ?? subjectTopics?.[0];

      selectTopic( subjectTopic, selector );
    }
  }, [topics]);
  const selectTopic = useCallback( async (topicSelector: string | ProgramSelector, subjectSelector?: string | SubjectSelector) => {
    if( !topicSelector ) return;

    const selector = toCamelCase(topicSelector) as ProgramSelector;
    setSelectedTopic( selector );

    await updateProgramCoachingData( selector, subjectSelector as SubjectSelector ); // TODO: Does this need really be awaited, and thus selectTopic need really be async?
  }, []);
  const selectService = useCallback( (serviceSelector: string) => { // TODO: TYPE PROPERLY
    if( !serviceSelector ) return;

    const selector = toCamelCase(serviceSelector) as string; // TODO: TYPE
    setSelectedService( selector );

    if( programCoachingData ) {
      const offer = programCoachingData?.offers.find( (offer: any) => { // TODO: TYPE
        return offer.serviceSelector === selector;
      });
      setSelectedOffer( offer );
    }
  }, [programCoachingData]);


  // ... Persisting State down into Children
  return (
    /* TODO: Split this up into other Contexts, such as one for ScheduleContext -- so not have such long & slightly irrelevant props passed via `value` prop */
    <CoachingContext.Provider value={ {coachingData, engagementsData, engagementOptions, optionContent, programCoachingData, selectedEngagement, selectedOption, subjects, topics, selectedSubject, selectedTopic, selectedService, selectedOffer} } >
      <CoachingUpdateContext.Provider value={ {selectEngagement, selectOption, selectSubject, selectTopic, selectService} } >

        {
          loading ?
            <h1>Loading...</h1>
          :
            children
        }

      </CoachingUpdateContext.Provider>
    </CoachingContext.Provider>
  )
}


// ================================= HELPERS =================================






/* */
